import supabase from '../config/supabase';
import config from '../config/environment';
import { handleServiceError } from '../utils/errorHandler';
import { enrichPerformanceRows } from '../utils/enrichPerformanceRows';
import { mapProjectIdentity } from '../utils/project';
import { readIsRecentlyReactivated } from '../utils/studentReactivation';
import {
  readIsPregnant,
  readIsRedFlag,
  readIsSensitive,
  readPregnantUntil,
  readSensitiveReason,
} from '../utils/studentSensitive';
import type { PerformanceFilters, PerformanceRow } from '../types/performance';
import type { PaginatedResponse } from '../types/student';

export type PerformancePaginatedResponse = PaginatedResponse<PerformanceRow>;

class PerformanceServiceClass {
  async listPaginated(
    page: number = 1,
    pageSize: number = 10,
    filters: PerformanceFilters = {},
  ): Promise<PerformancePaginatedResponse> {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    const url = new URL(`${config.supabaseFunctionsUrl}/list-paginated-performance`);
    url.searchParams.set('page', String(page));
    url.searchParams.set('pageSize', String(pageSize));

    if (filters.search) url.searchParams.set('search', filters.search);
    if (filters.coachIds?.length) url.searchParams.set('coachIds', filters.coachIds.join(','));
    if (filters.statuses?.length) url.searchParams.set('statuses', filters.statuses.join(','));
    if (filters.classifications?.length) {
      url.searchParams.set('classifications', filters.classifications.join(','));
    } else if (filters.filterStudentTenure && filters.filterStudentTenure !== 'all') {
      url.searchParams.set('filterStudentTenure', filters.filterStudentTenure);
    }
    if (filters.projectCodes?.length) url.searchParams.set('projectCodes', filters.projectCodes.join(','));
    if (filters.competence) url.searchParams.set('competence', filters.competence);
    const expectedStart = filters.expectedDeliveryDateStart ?? filters.dateStart;
    const expectedEnd = filters.expectedDeliveryDateEnd ?? filters.dateEnd;
    if (expectedStart) url.searchParams.set('expectedDeliveryDateStart', expectedStart);
    if (expectedEnd) url.searchParams.set('expectedDeliveryDateEnd', expectedEnd);
    if (filters.deliveredDateStart) url.searchParams.set('deliveredDateStart', filters.deliveredDateStart);
    if (filters.deliveredDateEnd) url.searchParams.set('deliveredDateEnd', filters.deliveredDateEnd);
    if (filters.sortOrder) url.searchParams.set('sortOrder', filters.sortOrder);

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: config.supabaseAnonKey,
      },
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      handleServiceError(new Error(err?.error || 'Erro ao listar performance'), 'listar performance');
    }

    const result = await response.json() as PerformancePaginatedResponse;
    result.data = (await enrichPerformanceRows(result.data)).map((row) => ({
      ...row,
      project: mapProjectIdentity(row.project) ?? mapProjectIdentity(row),
      is_reactivated: readIsRecentlyReactivated(row),
      is_sensitive: readIsSensitive(row),
      sensitive_reason: readSensitiveReason(row),
      is_pregnant: readIsPregnant(row),
      pregnant_until: readPregnantUntil(row),
      is_red_flag: readIsRedFlag(row),
    }));

    return result;
  }
}

export const PerformanceService = new PerformanceServiceClass();